import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faCircleXmark, faFileExcel, faUpload} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import saveAs from 'file-saver';
import {ApiService} from '../../services/api.service';
import {getFileNameFromResponse} from '../../../common/utils';
import {BulkImportResultComponent} from '../../shared/bulk-import-result/bulk-import-result.component';
import {
    BulkImportResultView,
    handleBulkImportError,
    handleBulkImportFallbackError,
} from '../../shared/bulk-import-result/bulk-import-result.utils';

type OrganizationOption = {
    id: number;
    name: string;
};

type SuccessSummary = {
    vehiclesCreated: number;
    initialOccupancyLogsCreated: number;
    organizationName: string;
};

@Component({
    selector: 'app-initial-occupancy-upload',
    imports: [CommonModule, FormsModule, FontAwesomeModule, BulkImportResultComponent],
    templateUrl: './initial-occupancy-upload.component.html',
    styleUrl: './initial-occupancy-upload.component.css',
})
export class InitialOccupancyUploadComponent implements OnInit {
    organizations: OrganizationOption[] = [];
    selectedOrganizationId: number | null = null;
    selectedFile: File | null = null;
    selectedFileName = '';
    loadingOrganizations = false;
    validating = false;
    uploading = false;
    bulkImportResult: BulkImportResultView | null = null;
    successSummary: SuccessSummary | null = null;
    showConfirmationModal = false;
    pendingRecordCount = 0;
    pendingOrganizationName = '';

    constructor(
        private apiService: ApiService,
        private toastr: ToastrService,
        library: FaIconLibrary,
    ) {
        library.addIcons(faFileExcel, faUpload, faCircleXmark);
    }

    ngOnInit(): void {
        this.loadOrganizations();
    }

    get selectedOrganizationName(): string {
        if (!this.selectedOrganizationId) {
            return '';
        }
        return this.organizations.find(org => org.id === this.selectedOrganizationId)?.name || '';
    }

    get canSubmit(): boolean {
        return !!this.selectedOrganizationId && !!this.selectedFile && !this.validating && !this.uploading;
    }

    loadOrganizations(): void {
        this.loadingOrganizations = true;
        this.apiService.getOrganizations(1000).subscribe({
            next: (response: any) => {
                const rows = Array.isArray(response?.results) ? response.results : (Array.isArray(response) ? response : []);
                this.organizations = rows
                    .map((org: any) => ({id: Number(org.id), name: org.name}))
                    .filter((org: OrganizationOption) => !!org.id && !!org.name)
                    .sort((a: OrganizationOption, b: OrganizationOption) => a.name.localeCompare(b.name));
                this.loadingOrganizations = false;
            },
            error: () => {
                this.loadingOrganizations = false;
                this.toastr.error('Unable to load organizations.', 'Error');
            },
        });
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        this.selectedFile = file;
        this.selectedFileName = file?.name || '';
        this.bulkImportResult = null;
        this.successSummary = null;
        this.showConfirmationModal = false;
    }

    downloadTemplate(): void {
        this.apiService.downloadImportTemplate('initial_occupancy').subscribe({
            next: (response) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err) => handleBulkImportFallbackError(err, this.toastr),
        });
    }

    beginUpload(): void {
        if (!this.selectedOrganizationId || !this.selectedFile) {
            this.toastr.error('Select an organization and file before uploading.', 'Validation');
            return;
        }

        this.validating = true;
        this.bulkImportResult = null;
        this.successSummary = null;

        this.apiService.validateInitialOccupancyUpload(this.selectedOrganizationId, this.selectedFile).subscribe({
            next: (response: any) => {
                this.validating = false;
                this.pendingRecordCount = Number(response?.records_found || response?.uploaded_records || 0);
                this.pendingOrganizationName = response?.organization_name || this.selectedOrganizationName;
                this.showConfirmationModal = true;
            },
            error: (err) => {
                this.validating = false;
                this.handleImportError(err);
            },
        });
    }

    confirmUpload(): void {
        if (!this.selectedOrganizationId || !this.selectedFile) {
            return;
        }

        this.uploading = true;
        this.showConfirmationModal = false;

        this.apiService.bulkUploadInitialOccupancy(this.selectedOrganizationId, this.selectedFile).subscribe({
            next: (response: any) => {
                this.uploading = false;
                this.successSummary = {
                    vehiclesCreated: Number(response?.vehicles_created || response?.uploaded_records || 0),
                    initialOccupancyLogsCreated: Number(
                        response?.initial_occupancy_logs_created || response?.uploaded_records || 0
                    ),
                    organizationName: response?.organization_name || this.selectedOrganizationName,
                };
                this.bulkImportResult = null;
                this.toastr.success(response?.message || 'Initial occupancy import completed.', 'Success');
            },
            error: (err) => {
                this.uploading = false;
                this.handleImportError(err);
            },
        });
    }

    cancelConfirmation(): void {
        this.showConfirmationModal = false;
    }

    private handleImportError(err: any): void {
        this.bulkImportResult = handleBulkImportError(err, this.toastr, 'initial_occupancy_upload_failures.csv');
    }
}
